import {
  Box,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VerifiedIcon from "@mui/icons-material/Verified";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import LockIcon from "@mui/icons-material/Lock";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet } from "react-router-dom";
import { getDoctorProfile } from "@/api/userApi";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

interface Props {
  role: "patient" | "doctor" | "admin";
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** When true, the item is rendered greyed-out and is not clickable. */
  disabled?: boolean;
}

const patientItems: NavItem[] = [
  { to: "/patient/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  {
    to: "/patient/appointments",
    label: "My appointments",
    icon: <EventAvailableIcon />,
  },
  { to: "/patient/payments", label: "Payments", icon: <PaymentIcon /> },
  { to: "/patient/profile", label: "Profile", icon: <PersonIcon /> },
];

const doctorItems: NavItem[] = [
  { to: "/doctor/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  {
    to: "/doctor/onboarding",
    label: "Onboarding",
    icon: <AssignmentIndIcon />,
  },
  {
    to: "/doctor/appointments",
    label: "Appointments",
    icon: <EventAvailableIcon />,
  },
  { to: "/doctor/schedule", label: "Availability", icon: <ScheduleIcon /> },
  { to: "/doctor/verification", label: "Verification", icon: <VerifiedIcon /> },
  { to: "/doctor/profile", label: "Profile", icon: <PersonIcon /> },
];

const adminItems: NavItem[] = [
  { to: "/admin/doctors", label: "Doctors", icon: <MedicalServicesIcon /> },
  { to: "/admin/appointments", label: "Appointments", icon: <EventAvailableIcon /> },
  { to: "/admin/refunds", label: "Refunds", icon: <CurrencyRupeeIcon /> },
];

// Items a doctor cannot use until an admin has approved their profile.
// These are the doctor-facing features that touch real patient bookings
// and the doctor's public availability.
const DOCTOR_APPROVED_ONLY_PATHS = new Set<string>([
  "/doctor/appointments",
  "/doctor/schedule",
]);

export function DashboardLayout({ role }: Props) {
  const { data: doctorProfile } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
    enabled: role === "doctor",
  });

  const doctorApproved =
    role !== "doctor" || doctorProfile?.profileStatus === "APPROVED";

  const baseItems =
    role === "doctor"
      ? doctorItems
      : role === "admin"
        ? adminItems
        : patientItems;

  const items: NavItem[] = baseItems.map((it) =>
    role === "doctor" &&
    !doctorApproved &&
    DOCTOR_APPROVED_ONLY_PATHS.has(it.to)
      ? { ...it, disabled: true }
      : it,
  );

  const labelByRole: Record<Props["role"], string> = {
    patient: "Patient",
    doctor: "Doctor",
    admin: "Admin",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />
      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
          alignItems: "start",
        }}
      >
        <Paper
          variant="outlined"
          sx={{ p: 1.5, position: { md: "sticky" }, top: { md: 88 } }}
        >
          <Typography
            variant="overline"
            sx={{ px: 1.5, color: "text.secondary" }}
          >
            {labelByRole[role]}
          </Typography>
          <List dense disablePadding>
            {items.map((it) => {
              if (it.disabled) {
                // Render a greyed-out, non-clickable entry with an
                // explanatory tooltip so the doctor understands *why* it's
                // unavailable and isn't just confused.
                return (
                  <Tooltip
                    key={it.to}
                    title="Available after admin approves your profile"
                    placement="right"
                    arrow
                  >
                    <span>
                      <ListItemButton
                        disabled
                        sx={{
                          borderRadius: 2,
                          my: 0.25,
                          opacity: 0.55,
                          "&.Mui-disabled": { opacity: 0.55 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {it.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={it.label}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        <LockIcon
                          fontSize="small"
                          sx={{ color: "text.disabled", ml: 1 }}
                        />
                      </ListItemButton>
                    </span>
                  </Tooltip>
                );
              }
              return (
                <ListItemButton
                  key={it.to}
                  component={NavLink}
                  to={it.to}
                  sx={{
                    borderRadius: 2,
                    my: 0.25,
                    "&.active": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "& .MuiListItemIcon-root": {
                        color: "primary.contrastText",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{it.icon}</ListItemIcon>
                  <ListItemText
                    primary={it.label}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
        </Paper>

        <Box component="main" sx={{ minWidth: 0 }}>
          <Outlet />
        </Box>
      </Container>
      <Box sx={{ mt: "auto" }}>
        <Footer />
      </Box>
    </Box>
  );
}
